/**
 * RiskCardContent - Reusable risk/signal display content.
 *
 * Shows a warning icon with risk title, associated entity subtitle,
 * description, category/severity tags, and an associated entity badge.
 *
 * Props-based: accepts pre-extracted string values so it works in both
 * CRM contexts (CrmObject) and proposal action contexts (flat payload).
 */

import { AlertTriangle } from '@untitledui/icons';
import { useCallback } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { stripAllMentionFormats } from '@/lib/markdown/entitySanitize';
import { cx } from '@/utils/cx';

import { CompactCardLayout } from './CompactCardLayout';

export interface RiskCardContentProps {
  /** Risk title */
  title: string;
  /** Description text */
  description?: string;
  /** Associated entity label (e.g. deal name) */
  associatedEntityLabel?: string;
  /** Associated entity type for the badge (e.g. "Deal update") */
  associatedEntityType?: string;
  /** Severity level (high/medium/low) */
  severity?: string;
  /** Risk category slug (e.g. "competitive_threat") */
  category?: string;
  /** Status (active/acknowledged/resolved/dismissed) */
  status?: string;
  /** When provided, clicking the title triggers this callback (e.g. open CRM slideout) */
  onClick?: () => void;
  /**
   * Compact mode — renders a single-line chip (entity-type pill + title)
   * suitable for side-by-side use in LinkedPair / MergeActionCard. Without
   * this, an association between a risk and another entity would render a
   * full RiskCardContent (warning icon + tags + description) inside the
   * paired row and blow up the side-by-side layout.
   */
  compact?: boolean;
  /** Compact mode only — omit the type pill (see CompactCardLayout). */
  hideTypeBadge?: boolean;
  className?: string;
  /** Org ID — enables clickable inline entity badges in the description. */
  orgId?: number;
}

/** Format a risk category slug to a human-readable label */
function formatCategoryLabel(category: string): string {
  return category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** Map severity to color classes (light + dark mode) */
function getSeverityColor(severity: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (severity) {
    case 'high':
      return {
        bg: 'bg-error-50 dark:bg-error-950',
        text: 'text-error-700 dark:text-error-300',
        border: 'border-error-200 dark:border-error-800',
      };
    case 'medium':
      return {
        bg: 'bg-warning-50 dark:bg-warning-950',
        text: 'text-warning-700 dark:text-warning-300',
        border: 'border-warning-200 dark:border-warning-800',
      };
    case 'low':
      return {
        bg: 'bg-success-50 dark:bg-success-950',
        text: 'text-success-700 dark:text-success-300',
        border: 'border-success-200 dark:border-success-800',
      };
    default:
      return {
        bg: 'bg-bg-secondary',
        text: 'text-fg-secondary',
        border: 'border-border-secondary',
      };
  }
}

export function RiskCardContent({
  title,
  description,
  associatedEntityLabel,
  associatedEntityType,
  severity,
  category,
  status,
  onClick,
  compact,
  hideTypeBadge,
  className,
  orgId,
}: RiskCardContentProps) {
  const handleTitleClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const strippedTitle = stripAllMentionFormats(title);
  const strippedAssociatedEntityLabel = associatedEntityLabel
    ? stripAllMentionFormats(associatedEntityLabel)
    : undefined;

  if (compact) {
    // Mirror the compact treatment the other content components use
    // (contact/company/deal) so risks fit into side-by-side paired rows.
    return (
      <CompactCardLayout
        name={strippedTitle}
        entityTypeName='ahoy_risk'
        hideTypeBadge={hideTypeBadge}
        onClick={onClick}
        className={className}
      />
    );
  }

  const severityColors = severity ? getSeverityColor(severity) : null;

  // Collect tags — category, severity, status
  const tags: { label: string; color: ReturnType<typeof getSeverityColor> }[] =
    [];

  if (category) {
    tags.push({
      label: formatCategoryLabel(category),
      color: getSeverityColor('medium'),
    });
  }
  if (severity) {
    tags.push({
      label: `${severity.charAt(0).toUpperCase()}${severity.slice(1)} severity`,
      color: getSeverityColor(severity),
    });
  }
  if (status && status !== 'active') {
    tags.push({
      label: status.charAt(0).toUpperCase() + status.slice(1),
      color: getSeverityColor(''),
    });
  }

  return (
    <div className={cx('space-y-3', className)}>
      {/* Header: warning icon + title + subtitle */}
      <div className='flex items-start gap-2.5'>
        <div
          className={cx(
            'flex items-center justify-center w-8 h-8 rounded-lg shrink-0 mt-0.5',
            severityColors ? severityColors.bg : 'bg-warning-50'
          )}
        >
          <AlertTriangle
            className={cx(
              'w-4 h-4',
              severityColors ? severityColors.text : 'text-warning-600'
            )}
          />
        </div>
        <div className='min-w-0 flex-1'>
          {onClick ? (
            <button
              type='button'
              onClick={handleTitleClick}
              className={cx(
                'text-sm font-semibold text-fg-primary leading-snug text-left',
                'bg-transparent border-0 p-0',
                'cursor-pointer hover:underline hover:text-fg-brand-primary'
              )}
            >
              {strippedTitle}
            </button>
          ) : (
            <span className='text-sm font-semibold text-fg-primary leading-snug'>
              {strippedTitle}
            </span>
          )}
          {strippedAssociatedEntityLabel && (
            <p className='text-xs text-fg-secondary mt-0.5'>
              {strippedAssociatedEntityLabel}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      {description && (
        <MarkdownContent
          content={description}
          orgId={orgId}
          size='compact'
          className='text-sm text-fg-secondary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0'
        />
      )}

      {/* Tags + associated entity badge */}
      {(tags.length > 0 || associatedEntityType) && (
        <div className='flex items-center flex-wrap gap-2'>
          {tags.map(tag => (
            <span
              key={tag.label}
              className={cx(
                'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border',
                tag.color.bg,
                tag.color.text,
                tag.color.border
              )}
            >
              {tag.label}
            </span>
          ))}
          {associatedEntityType && (
            <span className='ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border-secondary text-xs text-fg-secondary'>
              {associatedEntityType}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
